// src/components/Meetings.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabaseClient';
import { useParams, useLocation } from 'react-router-dom';
import MeetingCard from './MeetingCard';
import LegacyMeetingCard from './LegacyMeetingCard';
import './MeetingCard.css'; // Ensure we have styles for the load more button

function Meetings({ isWidget, parentInfo, onMeetingActive, isLegacy = false }) {
  const { abbreviation } = useParams();
  const location = useLocation();

  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true); // Initial load
  const [loadingMore, setLoadingMore] = useState(false); // "Load More" spinner
  const [error, setError] = useState(null);
  
  // Pagination State
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 20; // Number of meetings per batch

  const fetchMeetings = useCallback(async (pageToFetch) => {
    const isInitial = pageToFetch === 0;
    if (isInitial) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }

    try {
      let newMeetings = [];
      let totalCount = 0;

      // ---------------------------
      // A) WIDGET MODE (Secure Proxy)
      // ---------------------------
      if (isWidget) {
        const res = await fetch('/.netlify/functions/districtData', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            resource: 'meetings',
            abbreviation,
            parentOrigin: parentInfo?.origin,
            parentUrl: parentInfo?.url,
            page: pageToFetch,
            limit: LIMIT
          }),
        });

        if (res.status === 403) {
          throw new Error('Not authorized');
        }

        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          throw new Error(payload.error || 'Failed to load meetings');
        }

        const payload = await res.json();
        const data = payload.meetings || [];
        
        // Handle pagination metadata if present (from your updated backend)
        if (payload.pagination) {
          totalCount = payload.pagination.total;
        } else {
            // Fallback if backend isn't updated yet
            totalCount = data.length === LIMIT ? 9999 : meetings.length + data.length; 
        }

        newMeetings = (data || []).map((m) => ({
          ...m,
          content: m.summary || '',
        }));
      } 
      // ---------------------------
      // B) DIRECT MODE (Supabase)
      // ---------------------------
      else {
        const { data: district, error: distError } = await supabase
          .from('Districts')
          .select('id')
          .eq('abbreviation', abbreviation)
          .single();

        if (distError || !district) throw new Error('District not found');

        const from = pageToFetch * LIMIT;
        const to = from + LIMIT - 1;

        const { data, count, error } = await supabase
          .from('Meetings')
          .select('*', { count: 'exact' })
          .eq('districtId', district.id)
          .order('date', { ascending: false })
          .order('part', { ascending: true, nullsFirst: false })
          .range(from, to);

        if (error) throw error;

        totalCount = count;
        newMeetings = (data || []).map((m) => ({
          ...m,
          content: m.summary || '',
        }));
      }

      // ---------------------------
      // Update State
      // ---------------------------
      setMeetings(prev => isInitial ? newMeetings : [...prev, ...newMeetings]);
      
      // Determine if we have more to load
      // If we received fewer items than the limit, we've reached the end.
      const fetchedCount = newMeetings.length;
      if (fetchedCount < LIMIT) {
          setHasMore(false);
      } else if (totalCount && (meetings.length + fetchedCount >= totalCount)) {
          setHasMore(false);
      } else {
          setHasMore(true);
      }

    } catch (e) {
      console.error('Error fetching meetings:', e);
      setError({ message: e.message || 'Failed to load meetings' });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [abbreviation, isWidget, parentInfo, meetings.length]);

  // Initial Load
  useEffect(() => {
    setPage(0);
    setHasMore(true);
    fetchMeetings(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abbreviation, isWidget, parentInfo]);

  // Load More Handler
  const handleLoadMore = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchMeetings(nextPage);
  }, [page, fetchMeetings]);

  // Auto-load older pages when the URL hash targets a meeting that isn't
  // loaded yet (e.g. arriving from a search result for an older meeting that
  // normally sits behind "Load Older Meetings"). We page until the target
  // appears, then MeetingCard's own hash effect expands and scrolls to it.
  useEffect(() => {
    const hash = location.hash || '';
    if (!hash.startsWith('#meeting-')) return;
    if (loading || loadingMore || !hasMore) return;

    // Strip the optional `-pN` (part) and `-item-N` suffixes to get the date.
    const targetDate = hash
      .slice('#meeting-'.length)
      .replace(/-item-\d+$/, '')
      .replace(/-p\d+$/, '');
    if (!targetDate) return;

    const alreadyLoaded = meetings.some(
      (m) => (m.date || '').replace(/\//g, '-') === targetDate
    );
    if (alreadyLoaded) return;

    // Not loaded yet — pull the next page. This effect re-runs as `meetings`
    // grows, so it keeps paging until the target loads or hasMore is false.
    handleLoadMore();
  }, [location.hash, meetings, hasMore, loading, loadingMore, handleLoadMore]);

  if (loading) {
    return (
      <div className="content-loading-container">
        <div className="loading-spinner"></div>
        <p>Loading meetings...</p>
      </div>
    );
  }  
  
  if (error) return <p>Error loading meetings: {error.message}</p>;
  if (meetings.length === 0) return <p>No meetings found.</p>;

  const CardComponent = isLegacy ? LegacyMeetingCard : MeetingCard;

  return (
    <div className="meetings-list-container">
      <div className="meetings-list">
        {meetings.map((m) => {
          let parsedData = null;
          let finalAgendaItems = [];

          try {
            parsedData = typeof m.json_data === 'string' ? JSON.parse(m.json_data) : m.json_data;
          } catch (e) {
            console.error('Failed to parse JSON for meeting:', m.id);
          }

          if (parsedData?.agenda_items) {
            finalAgendaItems = [...parsedData.agenda_items];
          }

          if (Array.isArray(parsedData?.budget_discussions)) {
            const budgetByTs = new Map();
            parsedData.budget_discussions.forEach((b) => {
              if (b?.timestamp_start) budgetByTs.set(b.timestamp_start, b);
            });
            const unmatched = new Set(parsedData.budget_discussions);

            finalAgendaItems = finalAgendaItems.map((it) => {
              const b = it?.timestamp_start ? budgetByTs.get(it.timestamp_start) : null;
              if (b) {
                unmatched.delete(b);
                return { ...it, budget_detail: b };
              }
              return it;
            });

            unmatched.forEach((budget) => {
              finalAgendaItems.push({
                title: budget.title,
                timestamp_start: budget.timestamp_start,
                timestamp_end: budget.timestamp_end,
                action_timeline: { motion: { present: false }, vote: { present: false } },
                summary: {
                  context: budget.context,
                  debate: budget.board_debate,
                },
                budget_detail: budget,
              });
            });
          }

          if (parsedData?.public_comment_section?.exists) {
            const pc = parsedData.public_comment_section;
            finalAgendaItems.push({
              title: 'Public Comment (Non-Agenda Items)',
              timestamp_start: pc.timestamp_start,
              timestamp_end: pc.timestamp_end,
              summary: {
                context: pc.speaker_tally_summary || 'Public speakers addressed the board.',
                impact: pc.themes || [],
                themes: pc.themes || [],
                speakers: pc.speakers || [],
                debate: null,
                decision: null,
              },
            });
          }

          // Recording / backfill notice. Surfaced as the first section of the
          // expanded card so it's not silently dropped when agenda items exist.
          // Detection: meta.transcript_length_status starting with "no recording",
          // or meta.generated_by indicating a backfill/placeholder entry.
          const meta = parsedData?.meta || {};
          const statusText = typeof meta.transcript_length_status === 'string' ? meta.transcript_length_status : '';
          const generatedBy = typeof meta.generated_by === 'string' ? meta.generated_by : '';
          const isNoRecording = /^no recording/i.test(statusText);
          const isBackfill = /backfill|placeholder/i.test(generatedBy);
          const recordingNotice = (isNoRecording || isBackfill) ? (statusText || 'This entry was backfilled from the published agenda; no recording is available.') : null;

          return (
            <CardComponent
              key={`${m.id}-${m.part ?? 'nopart'}`}
              id={m.id}
              agendaItems={finalAgendaItems}
              date={m.date}
              title={m.title || 'Board Meeting'}
              topics={m.topics}
              content={m.content}
              isUpcoming={m.isUpcoming || m.meeting_type === 'upcoming'}
              meetingType={m.meeting_type}
              youtubeLink={m.youtube_link}
              part={m.part}
              isWidget={isWidget}
              onExpand={onMeetingActive}
              recordingNotice={recordingNotice}
            />
          );
        })}
      </div>

      {/* LOAD MORE BUTTON */}
      {hasMore && (
        <div className="load-more-container" style={{ textAlign: 'center', margin: '20px 0' }}>
          <button 
            className="load-more-btn" 
            onClick={handleLoadMore} 
            disabled={loadingMore}
            style={{
                padding: '10px 20px',
                fontSize: '14px',
                cursor: loadingMore ? 'not-allowed' : 'pointer',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ccc',
                borderRadius: '4px',
                color: '#333'
            }}
          >
            {loadingMore ? 'Loading...' : 'Load Older Meetings'}
          </button>
        </div>
      )}
    </div>
  );
}

export default Meetings;